import {AfterViewInit, Component, computed, input, OnDestroy, output} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {AppControlErrorComponent} from '@shared/components/forms/app-control-error/app-control-error.component';
import {BaseAppControlValueAccessorComponent} from '@shared/components/forms/app-control-value-accessor/base-app-control-value-accessor.component';
import {Editor, NgxEditorModule, Toolbar} from 'ngx-editor';
import {NgStyle} from "@angular/common";

@Component({
    selector: 'app-rich-text',
    templateUrl: './app-rich-text.component.html',
    styleUrls: ['./app-rich-text.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, FormsModule, TranslateModule, AppControlErrorComponent, NgxEditorModule, NgStyle]
})
export class AppRichTextComponent extends BaseAppControlValueAccessorComponent implements AfterViewInit, OnDestroy {

    // Inputs específicos do componente
    containerStyles = input<string>('');
    label = input<string>('');
    placeholder = input<string>('');
    hint = input<string>('');
    customErrors = input<Record<string, string>>({});
    showErrorDescription = input<boolean>(true);
    maxLength = input<number>();
    showCharacterCount = input<boolean>(true);
    toolbar = input<Toolbar>([
        // default value
        ['bold', 'italic'],
        ['underline', 'strike'],
        ['code', 'blockquote'],
        ['ordered_list', 'bullet_list'],
        [{heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']}],
        ['link', 'image'],
        // or, set options for link:
        //[{ link: { showOpenInNewTab: false } }, 'image'],
        ['text_color', 'background_color'],
        ['align_left', 'align_center', 'align_right', 'align_justify'],
        ['horizontal_rule', 'format_clear', 'indent', 'outdent'],
        ['superscript', 'subscript'],
        ['undo', 'redo'],
    ]);
    minHeight = input<number>(400); // in pixels

    // Outputs
    onContentChange = output<string>();
    onFocus = output<void>();
    onBlur = output<void>();

    // Editor instance
    editor: Editor;

    // Computed properties
    containerClasses = computed(() => {
        const classes = ['form-group'];
        if (this.containerStyles()) classes.push(this.containerStyles());
        return classes.join(' ');
    });

    editorClasses = computed(() => {
        const classes = ['NgxEditor__Wrapper'];
        if (this.hasErrors) classes.push('ng-invalid');
        if (this.isTouched) classes.push('ng-touched');
        if (this.isDirty) classes.push('ng-dirty');
        if (this.isDisabled()) classes.push('disabled');
        return classes.join(' ');
    });

    maxLengthFinal = computed(() => {
        return this.maxLength() || this.getValidatorMetadata()?.['maxlength']?.requiredLength || 5000;
    });

    characterCount = computed(() => {
        const content = this.getValue() || '';
        return this.getPlainText(content).length;
    });

    characterCountText = computed(() => {
        return `${this.characterCount()} / ${this.maxLengthFinal()}`;
    });

    isOverLimit = computed(() => {
        return this.characterCount() > this.maxLengthFinal();
    });

    constructor() {
        super();
        this.editor = new Editor({
            keyboardShortcuts: true,
            plugins: [],
            nodeViews: {}
        });
    }

    ngAfterViewInit(): void {
        this.setupControlListener();
    }

    override ngOnDestroy(): void {
        this.editor.destroy();
        super.ngOnDestroy();
    }

    onEditorContentChange(content: string): void {
        if (this.isDisabled()) return;

        // Verificar se o conteúdo está realmente vazio (apenas tags vazias)
        const plainText = this.getPlainText(content);

        // Se não há texto real, considerar como vazio
        const finalContent = plainText.trim().length === 0 ? '' : content;

        // Verificar limite de caracteres apenas se há conteúdo real
        if (finalContent && plainText.length > this.maxLengthFinal()) {
            return; // Não permite exceder o limite
        }

        this.setValue(finalContent);
        this.onContentChange.emit(finalContent);
    }

    onEditorFocus(): void {
        this.onFocus.emit();
    }

    onEditorBlur(): void {
        this.markAsTouched();
        this.onBlur.emit();
    }

    editorStyles = computed(() => {
        return {
            '--editor-height': `${this.minHeight()}px`
        };
    });

    // Métodos utilitários
    protected getPlainText(html: string): string {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.innerText || '';
    }

    private getValidatorMetadata(): any {
        const control = this.getControl();
        if (!control?.validator) return null;
        return control.validator(control);
    }

    // Método para acessibilidade
    getAriaDescribedBy(): string {
        const describedBy: string[] = [];

        if (this.hint()) {
            describedBy.push(this.inputId + '_hint');
        }

        if (this.showCharacterCount()) {
            describedBy.push(this.inputId + '_count');
        }

        if (this.hasErrors) {
            describedBy.push(this.inputId + '_error');
        }

        return describedBy.join(' ') || '';
    }

    // Métodos públicos para controle programático
    focus(): void {
        // Implementar foco no editor se necessário
        const editorElement = document.querySelector(`#${this.inputId} .ProseMirror`);
        if (editorElement) {
            (editorElement as HTMLElement).focus();
        }
    }

    clear(): void {
        this.setValue('');
        this.onContentChange.emit('');
    }

    // Getter/setter para compatibilidade com template
    get currentValue(): string {
        return this.getValue() || '';
    }

    set currentValue(value: string) {
        this.setValue(value);
    }
}
